'use client';

import { FileWarning, Clock, CheckCircle2, Download, BarChart3, FileText, Search, Calendar, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';

export function ProblemSolution() {
  const handleDownloadSample = () => {
    analytics.demoInteraction('sample_audit_pack_download_from_problem_solution');
    
    const link = document.createElement('a');
    link.href = '/api/demo/audit-pack';
    link.download = 'demo-workspace-audit-pack.zip';
    link.click();
  };

  return (
    <section className="py-24 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-foreground">
            Stop fighting fires. <br/>
            <span className="text-primary">Start controlling compliance.</span>
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Move from reactive spreadsheet management to a proactive system of record.
          </p>
        </div>

        {/* Split Comparison Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto items-stretch">
          
          {/* LEFT: The Scramble (Pain) */}
          <div className="relative rounded-3xl border border-border bg-muted/30 p-8 md:p-12 overflow-hidden group">
            {/* Background Noise */}
            <div className="absolute inset-0 opacity-[0.03] bg-[url('/grid.svg')] pointer-events-none" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                  <XCircle className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">The Audit Scramble</h3>
              </div>

              <div className="space-y-6">
                {/* Problem Cards - Tilted for 'Chaos' effect */}
                <div className="translate-x-2 rotate-1 transition-transform duration-500 group-hover:rotate-2 group-hover:translate-x-4">
          <ProblemCard 
            icon={<FileWarning className="w-5 h-5" />}
                    title="The Evidence Retrieval Gap"
            detail="An auditor asks for full history, the current certificate, and OOT records for one instrument. Your team scrambles across spreadsheets and shared drives for 20-30 minutes."
                    variant="danger"
          />
                </div>
                <div className="-translate-x-2 -rotate-1 transition-transform duration-500 group-hover:-rotate-2 group-hover:-translate-x-4">
          <ProblemCard 
            icon={<Search className="w-5 h-5" />}
                    title="The OOT Impact Assessment Gap"
            detail="A tool comes back out of tolerance, but the assessment window, potentially affected uses, and evidence gaps are captured inconsistently or not at all. Auditors know exactly where to probe."
                    variant="warning"
          />
                </div>
                <div className="translate-x-1 rotate-2 transition-transform duration-500 group-hover:rotate-3 group-hover:translate-x-2">
          <ProblemCard 
            icon={<Clock className="w-5 h-5" />}
                    title="The Overwritten Certificate Gap"
                    detail="You have the latest cert, but not the version active when a batch shipped. Traceability breaks when historical evidence is overwritten."
                    variant="danger"
          />
                </div>
        </div>

              <div className="mt-10 pt-8 border-t border-border/50">
                <p className="font-medium text-red-600/80 text-sm italic">
                  &ldquo;I spend more time managing the spreadsheet than managing the lab.&rdquo;
                </p>
              </div>
            </div>
        </div>

          {/* RIGHT: The System (Solution) */}
          <div className="relative rounded-3xl border-2 border-primary/20 bg-gradient-to-b from-background to-primary/5 p-8 md:p-12 shadow-xl shadow-primary/5 overflow-hidden">
            
            <div className="relative z-10 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <CheckCircle2 className="w-6 h-6" />
                        </div>
                <h3 className="text-2xl font-bold text-foreground">The System of Record</h3>
                    </div>
                    
              <div className="space-y-6 flex-1">
                {/* Solution 1: Automation */}
                <div className="flex items-start gap-4">
                  <div className="mt-1 p-1.5 rounded-md bg-primary/10 text-primary">
                    <Calendar className="w-4 h-4" />
                        </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Automated Scheduling</h4>
                    <p className="text-sm text-muted-foreground">Due dates track themselves. Alerts send automatically.</p>
                    </div>
                </div>

                {/* Solution 2: Evidence (Hero) */}
                <div className="bg-background rounded-xl border shadow-sm p-5 mt-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-md bg-blue-500/10 text-blue-600">
                            <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Audit-Ready Evidence Pack</h4>
                      <p className="text-xs text-muted-foreground">One-click ZIP generation.</p>
                </div>
            </div>

                  {/* Download CTA - Explicit Button Look */}
                  <button
                    onClick={handleDownloadSample}
                    className="w-full group flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50 hover:bg-primary/5 border border-border/50 hover:border-primary/30 transition-all cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-500 shrink-0">
                        <span className="font-bold text-[10px]">ZIP</span>
                        </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold truncate text-foreground">audit-pack-2025.zip</div>
                        <div className="text-[10px] text-muted-foreground">45.2 MB • Ready</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-background shadow-sm border text-[10px] font-medium text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Download className="w-3 h-3" />
                      Download
                    </div>
                  </button>
                </div>

                {/* Solution 3: Traceability */}
                <div className="flex items-start gap-4">
                  <div className="mt-1 p-1.5 rounded-md bg-primary/10 text-primary">
                    <BarChart3 className="w-4 h-4" />
                        </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Instant Traceability</h4>
                    <p className="text-sm text-muted-foreground">Full history and change logs available on demand.</p>
                  </div>
                </div>
                    </div>
                </div>
            </div>

        </div>
      </div>
    </section>
  );
}

// Sub-components
function ProblemCard({ icon, title, detail, variant = "default" }: { icon: React.ReactNode, title: string, detail: string, variant?: "default" | "danger" | "warning" }) {
  const variantStyles = {
    default: "border-border bg-background",
    danger: "border-red-200 bg-red-50/50 dark:bg-red-900/10 dark:border-red-900/30 text-red-900 dark:text-red-200",
    warning: "border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-900/30 text-amber-900 dark:text-amber-200"
  };

  return (
    <div className={cn("flex items-start gap-4 p-4 rounded-xl border shadow-sm transition-all hover:shadow-md", variantStyles[variant])}>
      <div className="mt-0.5 shrink-0 opacity-80">{icon}</div>
      <div>
        <div className="font-bold text-sm uppercase tracking-wide mb-1 opacity-90">{title}</div>
        <p className="text-sm opacity-80 leading-snug">{detail}</p>
      </div>
    </div>
  );
}
